export interface Match {
  id: number;
  eventId: number;
  opponentName?: string;
  homeScore?: number;
  awayScore?: number;
  isHomeMatch?: boolean;
  formation?: string;
  /** Futsal mérkőzés: a felállás alapból 4+1 fős. */
  isFutsal?: boolean;
  opponentNotes?: string;
  setPieceAttackNotes?: string;
  setPieceDefenseNotes?: string;
  otherNotes?: string;
  createdAt: string;
  updatedAt?: string;
  squad: MatchSquad[];
  lineup: MatchLineup[];
  subs: MatchSub[];
  minutesPlayed: MatchMinutesPlayed[];
  incidents: MatchIncident[];
}

export interface MatchSquad {
  id: number;
  matchId: number;
  playerId: number;
  playerName?: string;
  shirtNumber?: number;
}

export interface MatchLineup {
  id: number;
  matchId: number;
  playerId: number;
  playerName?: string;
  position?: string;
  shirtNumber?: number;
  individualInstruction?: string;
  pitchX?: number;
  pitchY?: number;
}

export interface MatchSub {
  id: number;
  matchId: number;
  playerId: number;
  playerName?: string;
  shirtNumber?: number;
  subOrder: number;
  position?: string;
}

export interface MatchMinutesPlayed {
  id: number;
  matchId: number;
  playerId: number;
  playerName?: string;
  minutesPlayed: number;
}

export enum MatchIncidentType {
  Goal = 0,
  Assist = 1,
  YellowCard = 2,
  RedCard = 3,
}

export interface MatchIncident {
  id: number;
  matchId: number;
  type: MatchIncidentType;
  playerId: number;
  playerName?: string;
  secondaryPlayerId?: number;
  secondaryPlayerName?: string;
  minute?: number;
}

export interface MatchCreateDto {
  opponentName?: string;
  homeScore?: number;
  awayScore?: number;
  isHomeMatch?: boolean;
  opponentNotes?: string;
  setPieceAttackNotes?: string;
  setPieceDefenseNotes?: string;
  otherNotes?: string;
}

export interface MatchUpdateDto {
  opponentName?: string;
  homeScore?: number;
  awayScore?: number;
  isHomeMatch?: boolean;
  isFutsal?: boolean;
  opponentNotes?: string;
  setPieceAttackNotes?: string;
  setPieceDefenseNotes?: string;
  otherNotes?: string;
}

export interface MatchSquadAddDto {
  playerId: number;
  shirtNumber?: number;
}

export interface MatchLineupItemDto {
  playerId: number;
  position?: string;
  shirtNumber?: number;
  individualInstruction?: string;
}

export interface MatchSubItemDto {
  playerId: number;
  shirtNumber?: number;
  subOrder: number;
}

export interface MatchMinutesPlayedItemDto {
  playerId: number;
  minutesPlayed: number;
}

export interface MatchIncidentCreateDto {
  type: MatchIncidentType;
  playerId: number;
  secondaryPlayerId?: number;
  minute?: number;
}

export interface MatchIncidentUpdateDto {
  type?: MatchIncidentType;
  playerId?: number;
  secondaryPlayerId?: number;
  minute?: number;
}

export interface PlayerMatchStatsDto {
  playerId: number;
  playerName?: string;
  matchesPlayed: number;
  totalMinutes: number;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
}

export enum MatchSelectionRole {
  Starter = 0,
  Sub = 1,
}

export interface MatchSelectionItemDto {
  playerId: number;
  role: MatchSelectionRole;
  shirtNumber?: number;
  position?: string;
  pitchX?: number;
  pitchY?: number;
}

export interface MatchSelectionDto {
  formation?: string;
  players: MatchSelectionItemDto[];
}

export interface MatchPlayerResultDto {
  playerId: number;
  minutesPlayed?: number;
  goals: number;
  assists: number;
  yellowCards: number;
  redCards: number;
}

export interface MatchResultDto {
  homeScore?: number;
  awayScore?: number;
  /** Hazai pályán játszottunk-e (kihagyva = nem változik). */
  isHomeMatch?: boolean;
  players: MatchPlayerResultDto[];
}

export type MatchOutcome = 'win' | 'draw' | 'loss';

/**
 * A meccs kimenetele a mi szemszögünkből. Az eredmény a valós sorrendet
 * követi (hazai – vendég), ezért idegenben a vendég oldali gól a miénk.
 * Null, ha nincs teljes eredmény, vagy nem tudjuk, hazai volt-e a meccs.
 */
export const getMatchOutcome = (m: {
  homeScore?: number | null;
  awayScore?: number | null;
  isHomeMatch?: boolean | null;
}): MatchOutcome | null => {
  if (m.homeScore == null || m.awayScore == null || m.isHomeMatch == null) return null;
  const ours = m.isHomeMatch ? m.homeScore : m.awayScore;
  const theirs = m.isHomeMatch ? m.awayScore : m.homeScore;
  if (ours > theirs) return 'win';
  if (ours < theirs) return 'loss';
  return 'draw';
};

export interface ShirtSuggestion {
  playerId: number;
  candidates: { number: number; count: number; score: number }[];
}

export interface SelectablePlayer {
  id: number;
  name: string;
  ageGroups: string[];
}

export interface FormationTemplate {
  id: number;
  name: string;
  teamSize: number;
  slots: { x: number; y: number; role: string }[];
  createdByName?: string;
  canDelete: boolean;
}

export interface MatchListItem {
  id: number;
  eventId: number;
  eventTitle?: string;
  startTime: string;
  ageGroup?: string;
  ageGroupId?: number;
  opponentName?: string;
  homeScore?: number;
  awayScore?: number;
  isHomeMatch?: boolean;
  isFutsal?: boolean;
}

export const getMatchIncidentTypeLabel = (type: MatchIncidentType): string => {
  switch (type) {
    case MatchIncidentType.Goal:
      return 'Gól';
    case MatchIncidentType.Assist:
      return 'Gólpassz';
    case MatchIncidentType.YellowCard:
      return 'Sárga lap';
    case MatchIncidentType.RedCard:
      return 'Piros lap';
    default:
      return 'Ismeretlen';
  }
};
